














































































































































import Vue from 'vue';
import { Entity, Types, Type, ObjectType, Expression, Traverser, ReturnExpression, Func, DefinitionsFunctionReference } from 'expangine-runtime';
import { TypeUpdateEvent, TypeSettings } from '../runtime/types/TypeVisuals';
import { Preferences, PreferenceCategory } from './Preference';
import { LiveRuntime } from 'expangine-runtime-live';
import { getConfirmation } from './Confirm';
import { Registry } from '../runtime/Registry';
import { sendNotification } from './Notify';
import { getFuncTest } from './FuncTest';



const PREF_REMOVE_METHOD = Preferences.define({
  key: 'method_remove',
  label: 'Remove method without confirmation',
  category: [PreferenceCategory.CONFIRM],
  defaultValue: false,
});

export default Vue.extend({
  props: {
    method: {
      type: Object as () => Func,
      required: true,
    },
    entity: {
      type: Object as () => Entity,
      required: true,
    },
    registry: {
      type: Object as () => Registry,
      required: true,
    },
  },
  data: () => ({
    tab: 0,
    requiredParamsType: Types.object() as Type,
  }),
  computed: {
    settings(): TypeSettings<any> {
      return this.registry.getTypeSettings(this.methodContext);
    },
    hasValidParams(): boolean {
      return this.requiredParamsType.acceptsType(this.method.params);
    },
    hasReturn(): boolean {
      return 0 < this.method.expression.traverse(
        Traverser.count<Expression>().filterClass(ReturnExpression),
      );
    },
    references(): DefinitionsFunctionReference[] {
      return this.registry.defs.getMethodReferences(this.entity, this.method);
    },
    hasTests(): boolean {
      return this.method.tests.length > 0 && this.tab === 4;
    },
    methodContext(): Type {
      return this.registry.defs.getContextWithScope(this.method.params, {
        [Expression.INSTANCE]: this.entity.type,
      }).context;
    },
  },
  methods: {
    renamed(newName: string) {
      const { registry, method, entity } = this;
      const { defs } = registry;

      if (method.name) {
        const updates = defs.renameMethod(entity.name, method.name, newName);

        if (updates && updates.length) {
          sendNotification({ message: `${updates.length} Method reference(s) updated.` });
        }
      } else {
        method.name = newName;

        defs.addMethod(entity, method);
      }
    },
    validateName(name: string) {
      if (!name) {
        return 'A name is required.';
      }

      const existing = this.entity.methods[name];
      if (existing && existing !== this.method) {
        return 'A method already exists with that name.';
      }

      return '';
    },
    async remove() {
      const { registry, method, entity } = this;
      const { defs } = registry;

      const refs = defs.getMethodReferences(entity, method).length;

      if (!await getConfirmation({ message: `Are you sure you want to remove this method? It is referenced ${refs} times.`, pref: PREF_REMOVE_METHOD })) {
        return;
      }

      if (!defs.removeMethod(entity, method)) {
        sendNotification({ message: 'You cannot remove a referenced method. '});
      } else {
        this.$emit('remove', method);
      }
    },
    async addTest() {
      const { method: func, registry } = this;

      const test = await getFuncTest({
        func,
        test: {
          name: '',
          description: '',
          args: func.params.create(),
          expected: null,
        },
        registry,
      });

      if (!test) {
        sendNotification({ message: 'Add Unit Test canceled.' });
      }
    },
    onChange(event: TypeUpdateEvent) {
      this.method.params = event.type as ObjectType;
      this.method.meta = event.settings;

      if (event.transform) {
        this.method.refactor(event.transform, LiveRuntime);
      }
    },
    onProgramChange(program: Expression) {
      this.method.expression = program;
    },
    onArgumentRemove(arg: string) {
      const { registry, method, entity } = this;
      const { defs } = registry;

      defs.removeMethodParameter(entity, method, arg);
    },
    onArgumentRename([oldProp, newProp]: [string, string]) {
      const { registry, method, entity } = this;
      const { defs } = registry;

      defs.renameMethodParameter(entity, method, oldProp, newProp);
    },
  },
});
